import React from "react";
import { useEffect, useRef } from "react";

import { Icon } from "@braze/beacon-core";

import { lookupOptionsDescription, lookupOptionsParameters } from "@lib/agenticAi/sharedToolContracts";

import useI18nNamespace from "@services/useI18nNamespace";
import { useRegisterChangeTool } from "@services/useRegisterChangeTool";

import { isSelectOptionDisabled } from "../utils/isSelectOptionDisabled";
import type { UseSearchToolParams } from "./types";

/**
 * Hook to register a search/lookup tool for operator.
 * Shared between BrazeSelect and BrazeSelectAsyncCreatable.
 */
export const useSearchTool = <OptionType,>({
  id,
  isDisabled,
  operatorDisabledReason,
  searchFn,
  updateCachedOptions,
  formatOptionStringLabel,
}: UseSearchToolParams<OptionType>) => {
  const { t } = useI18nNamespace("braze-select");
  const registerChangeTool = useRegisterChangeTool({ operatorDisabledReason });

  const searchFnRef = useRef(searchFn);
  searchFnRef.current = searchFn;
  const formatOptionStringLabelRef = useRef(formatOptionStringLabel);
  formatOptionStringLabelRef.current = formatOptionStringLabel;

  const hasSearchFn = Boolean(searchFn);

  useEffect(() => {
    if (isDisabled || !hasSearchFn) {
      return;
    }

    const cleanup = registerChangeTool({
      toolEntityId: id,
      name: "lookup_options",
      description: lookupOptionsDescription,
      parameters: lookupOptionsParameters,
      callback: async ({ searchQuery }) => {
        const currentSearchFn = searchFnRef.current;
        if (!currentSearchFn) {
          throw new Error("Search is not available for this dropdown");
        }

        const options = (await currentSearchFn(searchQuery)).filter((option) => !isSelectOptionDisabled(option));
        updateCachedOptions(options);

        if (options.length === 0) {
          return `No options found for search query "${searchQuery}".`;
        }

        const optionValues = options.map((opt) => formatOptionStringLabelRef.current(opt));
        return `Found ${options.length} option(s) for search query "${searchQuery}". Use one of these values with the select tool:\n${optionValues.join("\n")}`;
      },
      options: {
        toolGroup: "Agentic Creation & Editing",
        labels: {
          ready: t("lookup-options-ready", "Preparing to search"),
          running: t("lookup-options-running", "Searching"),
          complete: t("lookup-options-complete", "Search complete"),
          failed: t("lookup-options-failed", "Search failed"),
          stopped: t("lookup-options-stopped", "Search stopped"),
        },
        requiresApproval: "no",
        argumentsSummaryText: ({ searchQuery }) => searchQuery,
        toolIcon: <Icon name="search" />,
      },
    });

    return cleanup;
  }, [registerChangeTool, id, t, isDisabled, hasSearchFn, updateCachedOptions]);
};
